import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import { optClasses } from '../optionColor';
import { ListStateShell } from '../ListStateShell';
import { useListFetch } from '../useListFetch';
import { TicketDetailPanel } from '../TicketDetailPanel';

/**
 * WhatsAppTicketsCard — Part 2 of the real-estate module (realestate-dashboard-prd.md §2.2, AC2.2).
 * Follows Part 1's section-component pattern (RentRollCard.tsx) — its own file, PortfolioTab composes
 * it. Real Tickets that carry a unit (the additive `Ticket.unitId`, §2.1) AND actually trace back to a
 * `channel='whatsapp'` Conversation (GET /api/tickets/from-whatsapp, TicketService.
 * getWhatsAppLinkedTickets) — never the mock `WHATSAPP_ITEMS` const. Each row opens the real
 * TicketDetailPanel, same end-anchored overlay idiom §0.4 already specifies for a record opened from
 * inside another surface.
 */

interface WhatsAppTicketRow {
  id: string;
  number: string;
  subject: string;
  tenantName: string | null;
  assigneeName: string | null;
  createdAt: string;
}

export const WhatsAppTicketsCard: React.FC = () => {
  const { t } = useTranslation('common');
  const { status, data, retry } = useListFetch<WhatsAppTicketRow[]>('/api/tickets/from-whatsapp');
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);

  return (
    <div className="bg-surface border border-border-neutral rounded-xl overflow-hidden" data-testid="portfolio-whatsapp-card">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-neutral">
        <h3 className="text-sm font-bold text-text-strong m-0">{t('portfolio.fromWhatsApp.title')}</h3>
      </div>

      <ListStateShell status={status} testIdBase="portfolio-whatsapp" onRetry={retry}>
        {!data || data.length === 0 ? (
          <div className="px-4 py-6 text-sm text-text-muted" data-testid="portfolio-whatsapp-empty">{t('portfolio.fromWhatsApp.empty')}</div>
        ) : (
          <div className="divide-y divide-border-neutral">
            {data.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setOpenTicketId(item.id)}
                className="flex items-start gap-3 px-4 py-3 w-full text-start hover:bg-canvas"
                data-testid="portfolio-whatsapp-row"
              >
                <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${optClasses('violet').bg} ${optClasses('violet').fg}`}>
                  <MessageSquare className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-text-strong leading-snug truncate">{item.subject}</div>
                  <div className="text-xs text-text-muted leading-snug mt-0.5 truncate">
                    {item.tenantName ?? t('portfolio.fromWhatsApp.unknownTenant')}
                    {item.assigneeName ? ` · ${t('portfolio.fromWhatsApp.assignedTo', { name: item.assigneeName })}` : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </ListStateShell>

      {openTicketId && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50" data-testid="portfolio-whatsapp-ticket-overlay">
          <div className="bg-canvas h-full w-full lg:w-96 lg:shrink-0 overflow-y-auto">
            <TicketDetailPanel
              ticketId={openTicketId}
              sheetShape={typeof window !== 'undefined' && window.innerWidth < 768 ? 'sheet' : 'popover'}
              onClose={() => setOpenTicketId(null)}
              onCreated={() => {}}
              onUpdated={() => {}}
            />
          </div>
        </div>
      )}
    </div>
  );
};
